import React, { MouseEventHandler } from 'react';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { DealUIObject } from 'common/api/deals/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags, faTicket } from '@fortawesome/pro-solid-svg-icons';
import { createStyles } from 'utils/createStyle';

export interface DealCardProps {
    deal: DealUIObject;
    brand: Brand | undefined;
    onClick: MouseEventHandler<HTMLButtonElement>;
}

const dealCardStyles = createStyles({
    cardButton: ['mb-0 w-full md:w-1/2 min-w-[319px]'],
    cardContainer: [
        'flex flex-col items-center mb-1 md:mb-2 border p-[20px] m-2 rounded-[8px] relative',
    ],
    cardContainerBonus: [
        'flex flex-col items-center mb-2 border border-bonus-pink pt-[8px] md:pt-[20px] p-[20px] pb-[8px] m-2 rounded-[8px] relative',
    ],
    cardContainerRegular: [
        'flex flex-col items-center mb-2 border border-grey-light pt-[8px] md:pt-[20px] p-[20px] pb-[8px] m-2 rounded-[8px] relative',
    ],
    headerRow: ['flex flex-row align-start items-center w-full py-[5px] h-[52px]'],
    brandImageContainer: ['mr-2 basis-[75px] text-left'],
    brandImage: ['inline-block md:w-16 md:max-h-[42px]'],
    dealTypeTag: [
        'text-white text-[12px] lineheight-[12px] font-semibold bg-bonus-green rounded-full px-2 h-[18px] ml-2 md:ml-0',
    ],
    dealTypeIcon: ['text-white'],
    flexGrow: ['flex-grow'],
    earningText: ['text-[12px] md:text-[14px] text-grey-darkest'],
    strikethrough: ['line-through'],
    bonusValue: ['text-dark-blue text-[16px] md:text-[18px] font-semibold pl-1'],
    rebateValue: ['text-dark-blue text-[18px] font-semibold'],
    titleContainer: ['mt-[5px] w-full mb-1 flex flex-row justify-start min-h-[48px]'],
    title: [
        'text-grey-darkest text-[16px] font-semibold text-left line-clamp-2 overflow-hidden text-ellipsis max-h-[48px]',
    ],
    bonusBanner: [
        'absolute top-0 right-0 text-[10px] md:text-[11px] bg-bonus-pink text-white uppercase font-semibold px-[40px] py-[4px] rounded-bl-lg rounded-tr-lg',
    ],
});

export const DealCard = ({ deal, brand, onClick }: DealCardProps) => {
    return (
        <button className={dealCardStyles.cardButton} onClick={onClick}>
            <div
                className={
                    brand && brand.affiliateBonus
                        ? dealCardStyles.cardContainerBonus
                        : dealCardStyles.cardContainerRegular
                }
            >
                <div className={dealCardStyles.headerRow}>
                    <div className={dealCardStyles.brandImageContainer}>
                        {brand && brand.imageUrl ? (
                            <img src={brand.imageUrl} className={dealCardStyles.brandImage} />
                        ) : (
                            <div className={dealCardStyles.brandImage} />
                        )}
                    </div>

                    <div className={dealCardStyles.dealTypeTag}>
                        {deal.dealType === 'coupon' ? (
                            <>
                                <FontAwesomeIcon
                                    icon={faTicket}
                                    className={dealCardStyles.dealTypeIcon}
                                />{' '}
                                Coupon{' '}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon
                                    icon={faTags}
                                    className={dealCardStyles.dealTypeIcon}
                                />{' '}
                                Deal{' '}
                            </>
                        )}
                    </div>

                    <div className={dealCardStyles.flexGrow} />
                    {brand && (
                        <div className="self-center">
                            <span className={dealCardStyles.earningText}>
                                Earn
                                {brand && brand.affiliateExclusionText ? ' up to ' : ' '}
                                {brand && brand.affiliateBonus ? (
                                    <>
                                        <span className={dealCardStyles.strikethrough}>
                                            {brand && brand?.affiliateRebate}%
                                        </span>
                                        <span className={dealCardStyles.bonusValue}>
                                            {brand && brand?.affiliateBonus}%
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className={dealCardStyles.rebateValue}>
                                            {brand && brand?.affiliateRebate}%
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    )}
                </div>

                <div className={dealCardStyles.titleContainer}>
                    <h4 className={dealCardStyles.title}>{deal.title}</h4>
                </div>

                {brand && brand.affiliateBonus && (
                    <div className={dealCardStyles.bonusBanner}>Bonus</div>
                )}
            </div>
        </button>
    );
};
